/**
 * Payout Service - Frontend service for payout operations
 * Uses shared API client from @/shared/api
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Preview immediate payout run
 * @param {string} jobId - Job ID
 * @param {string} payeeId - Payee ID
 * @param {Object} feeOverrides - Optional fee overrides
 * @returns {Promise<Object>} Preview result
 */
export const previewImmediatePayoutRun = async (jobId, payeeId, feeOverrides = null) => {
  try {
    const response = await api.post("/payouts/runs/immediate/preview", {
      jobId,
      payeeId,
      feeOverrides,
    });
    logInfo("Payout preview fetched successfully", { jobId, payeeId });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to preview immediate payout run", {
      jobId,
      payeeId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Preview cycle payout run
 * @param {string} payeeId - Payee ID
 * @param {string} cycleStart - Cycle start date (ISO string)
 * @param {string} cycleEnd - Cycle end date (ISO string)
 * @param {Object} feeOverrides - Optional fee overrides
 * @returns {Promise<Object>} Preview result
 */
export const previewCyclePayoutRun = async (payeeId, cycleStart, cycleEnd, feeOverrides = null) => {
  try {
    const response = await api.post("/payouts/runs/cycle/preview", {
      payeeId,
      cycleStart,
      cycleEnd,
      feeOverrides,
    });
    logInfo("Cycle payout preview fetched successfully", { payeeId, cycleStart, cycleEnd });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to preview cycle payout run", {
      payeeId,
      cycleStart,
      cycleEnd,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Create immediate payout run
 * @param {string} jobId - Job ID
 * @param {string} payeeId - Payee ID
 * @param {Object} feeOverrides - Optional fee overrides
 * @returns {Promise<Object>} Creation result
 */
export const createImmediatePayoutRun = async (jobId, payeeId, feeOverrides = null) => {
  try {
    const response = await api.post("/payouts/runs/immediate", {
      jobId,
      payeeId,
      feeOverrides,
    });
    logInfo("Immediate payout run created successfully", {
      payoutRunId: response.data?.run?.id,
      jobId,
      payeeId,
    });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to create immediate payout run", {
      jobId,
      payeeId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Create cycle payout run
 * @param {string} payeeId - Payee ID
 * @param {string} cycleStart - Cycle start date (ISO string)
 * @param {string} cycleEnd - Cycle end date (ISO string)
 * @param {Object} feeOverrides - Optional fee overrides
 * @returns {Promise<Object>} Creation result
 */
export const createCyclePayoutRun = async (payeeId, cycleStart, cycleEnd, feeOverrides = null) => {
  try {
    const response = await api.post("/payouts/runs/cycle", {
      payeeId,
      cycleStart,
      cycleEnd,
      feeOverrides,
    });
    logInfo("Cycle payout run created successfully", {
      payoutRunId: response.data?.run?.id,
      payeeId,
    });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to create cycle payout run", {
      payeeId,
      cycleStart,
      cycleEnd,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get payout run by ID
 * @param {string} payoutRunId - Payout run ID
 * @returns {Promise<Object>} Payout run details
 */
export const getPayoutRunById = async (payoutRunId) => {
  try {
    const response = await api.get(`/payouts/runs/${payoutRunId}`);
    logInfo("Payout run fetched successfully", { payoutRunId });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to get payout run", {
      payoutRunId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get payout runs list
 * @param {Object} filters - Filter criteria
 * @param {string} filters.payeeId - Payee ID filter
 * @param {string} filters.from - Start date filter
 * @param {string} filters.to - End date filter
 * @param {number} filters.limit - Limit
 * @param {number} filters.offset - Offset
 * @returns {Promise<Object>} Payout runs list
 */
export const getPayoutRuns = async (filters = {}) => {
  try {
    const params = {};
    if (filters.payeeId) params.payeeId = filters.payeeId;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;

    const response = await api.get("/payouts/runs", { params });
    logInfo("Payout runs fetched successfully", { count: response.data?.items?.length || 0 });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to get payout runs", {
      filters,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      data: { items: [], pagination: { total: 0, limit: 20, offset: 0 } },
    };
  }
};

/**
 * Get provider payout summary
 * @param {string} providerId - Provider ID
 * @returns {Promise<Object>} Provider payout summary
 */
export const getProviderPayoutSummary = async (providerId) => {
  try {
    const response = await api.get(`/payouts/providers/${providerId}/summary`);
    logInfo("Provider payout summary fetched successfully", { providerId });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to get provider payout summary", {
      providerId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Provider transaction history (settlement-aware)
 */
export const getProviderTransactions = async (payeeId, filters = {}) => {
  try {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.status) params.status = filters.status;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;
    const response = await api.get(`/payouts/providers/${payeeId}/transactions`, {
      params,
    });
    return { success: true, data: response.data || response };
  } catch (error) {
    logError("Failed to get provider transactions", {
      payeeId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      data: { items: [], pagination: { total: 0 } },
    };
  }
};

export const getProviderStatement = async (payeeId, filters = {}) => {
  try {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.includePaid !== undefined) params.includePaid = filters.includePaid;
    if (filters.includeUnpaid !== undefined)
      params.includeUnpaid = filters.includeUnpaid;
    const response = await api.get(`/payouts/providers/${payeeId}/statement`, {
      params,
    });
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Trigger browser download from a binary API Response (must use api.binary —
 * api.get parses non-JSON as text and corrupts PDF bytes).
 * @param {Response} response
 * @param {string} fallbackFilename
 */
const downloadBlobFromResponse = async (response, fallbackFilename) => {
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fallbackFilename;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }

  const blob = await response.blob();
  const signature = await blob.slice(0, 5).text();
  if (signature !== "%PDF-") {
    throw new Error("Download did not return a valid PDF");
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadProviderStatementPdf = async (payeeId, filters = {}) => {
  try {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.includePaid !== undefined)
      params.includePaid = String(filters.includePaid);
    if (filters.includeUnpaid !== undefined)
      params.includeUnpaid = String(filters.includeUnpaid);
    const response = await api.binary(
      `/payouts/providers/${payeeId}/statement.pdf`,
      params,
      "application/pdf"
    );
    await downloadBlobFromResponse(
      response,
      `provider-statement-${payeeId}.pdf`
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const emailProviderStatement = async (payeeId, payload = {}) => {
  try {
    const response = await api.post(
      `/payouts/providers/${payeeId}/statement/email`,
      payload
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      code: error.response?.data?.code,
    };
  }
};

export const getRunDeliveryOptions = async (payoutRunId) => {
  try {
    const response = await api.get(
      `/payouts/runs/${payoutRunId}/delivery-options`
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const initiatePayment = async (payoutRunId, payload = {}) => {
  try {
    const response = await api.post(
      `/payouts/runs/${payoutRunId}/initiate-payment`,
      payload
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const confirmProviderPayment = async (payoutRunId, payload = {}) => {
  try {
    const response = await api.post(
      `/payouts/runs/${payoutRunId}/confirm`,
      payload
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const uploadPayoutPop = async (payoutRunId, file) => {
  try {
    const formData = new FormData();
    formData.append("proofOfPayment", file);
    const response = await api.post(
      `/payouts/runs/${payoutRunId}/pop`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const finalizePayoutSettlement = async (payoutRunId, payload = {}) => {
  try {
    const response = await api.post(
      `/payouts/runs/${payoutRunId}/finalize`,
      payload
    );
    return { success: true, data: response.data || response };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const downloadPayoutOfferPdf = async (payoutRunId) => {
  try {
    const response = await api.binary(
      `/payouts/runs/${payoutRunId}/offer.pdf`,
      {},
      "application/pdf"
    );
    await downloadBlobFromResponse(
      response,
      `payout-offer-${payoutRunId}.pdf`
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
